import {useDrag, useDrop} from "react-dnd";
import React from "react";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";

const ItemTypes = {
  TAG: "tag",
} as const;

interface DragItem {
  type: typeof ItemTypes.TAG;
  id: number;
  index: number;
}
interface IssueOverrideTagItemProps {
  id: number;
  name: string;
  index: number;
  onRemove: (id: number) => void;
  handleDrop: (id: number, newOrder: number) => void;
  handleDragMove: (dragIndex: number, hoverIndex: number) => void;
}

const styles: Record<string, React.CSSProperties> = {
  listItem: {
    backgroundColor: "#f5f5f5",
    marginBottom: "0.5rem",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "move",
    transition: "all 0.2s ease",
  },
};

const IssueOverrideTagItem: React.FC<IssueOverrideTagItemProps> = ({
  id,
  name,
  index,
  onRemove,
  handleDragMove,
  handleDrop,
}) => {
  const [{isDragging}, drag] = useDrag({
    item: {type: ItemTypes.TAG, id, index} as DragItem,
    collect: monitor => ({
      isDragging: Boolean(monitor.isDragging()),
    }),
  });

  const [{isOver}, drop] = useDrop({
    accept: ItemTypes.TAG,
    collect: monitor => ({
      isOver: Boolean(monitor.isOver()),
    }),
    hover: (draggedItem: DragItem, monitor) => {
      if (!monitor.isOver({shallow: true})) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      handleDragMove(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
    drop: (draggedItem: DragItem) => {
      if (draggedItem.index !== draggedItem.id) {
        handleDrop(draggedItem.id, index);
      }
    },
  });

  const ref = (node: HTMLElement | null) => {
    drag(drop(node));
  };

  return (
    <ListItem
      ref={ref}
      style={{
        ...styles.listItem,
        opacity: isDragging ? 0.5 : 1,
        transform: isOver ? "scale(1.01)" : "scale(1)",
      }}
    >
      <ListItemText primary={name} />
      <IconButton edge="end" aria-label="delete" onClick={() => onRemove(id)}>
        <DeleteIcon />
      </IconButton>
    </ListItem>
  );
};

export default IssueOverrideTagItem;
